import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Stack, Typography, TextField, MenuItem, Grid,
  ToggleButton, ToggleButtonGroup, alpha, Chip, CircularProgress, Button,
} from '@mui/material';
import {
  DirectionsCar, TwoWheeler, LocalTaxi, AirportShuttle, MyLocation, Bolt, Schedule, CheckCircle, Person, Group,
} from '@mui/icons-material';
import LocationSearchField from '../maps/LocationSearchField';
import DispatchRouteMap, { type RouteMapMarker } from './DispatchRouteMap';
import { useDispatchCustomerPhone } from './DispatchCustomerPhoneStep';
import DispatchPartnerAssignStep from './DispatchPartnerAssignStep';
import { useCurrentLocation } from '../../hooks/useCurrentLocation';
import { reverseGeocode, type LocationResult } from '../../services/locationService';
import { bookSegSx } from './dispatchTheme';
import { BookRailLabel } from './DispatchBookChrome';
import PersonNameField from '../ui/forms/PersonNameField';
import PhoneNumberField from '../ui/forms/PhoneNumberField';
import { validatePersonName, validatePhoneNumber } from '../../utils/personFieldValidation';
import {
  haversineKm, estimateDurationMin, formatCurrency,
  type DispatchMode, type VehicleFareOption,
} from './dispatchBookUtils';
import { dispatchRide, estimateTransportFare, getTenantServices } from '../../services/businessService';
import { getMasters } from '../../services/platform/masterService';
import { getBookingRentalPackages } from '../../services/rentalManagementService';

type TripKind = 'LOCAL' | 'RENTAL' | 'OUTSTATION';
type ScheduleMode = 'RIDE_NOW' | 'RIDE_LATER';

function localDateTimeValue(offsetMs = 60 * 60 * 1000) {
  const soon = new Date(Date.now() + offsetMs);
  return new Date(soon.getTime() - soon.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

interface RentalPackageOption {
  id: string;
  name: string;
  packageHr: number;
  packageKm: number;
  price: number;
  vehicleTypeCode: string;
  packageGroupCode: string;
  packageRowId?: string;
}

const VEHICLE_ICONS: Record<string, typeof DirectionsCar> = {
  bike: TwoWheeler,
  auto: LocalTaxi,
  suv: AirportShuttle,
};

interface DispatchRideBookWizardProps {
  onSuccess: () => void;
  onError: (msg: string) => void;
  setLoading: (v: boolean) => void;
  setCanSubmit: (v: boolean) => void;
  registerSubmit: (fn: () => Promise<void>) => void;
}

export default function DispatchRideBookWizard({
  onSuccess, onError, setLoading, setCanSubmit, registerSubmit,
}: DispatchRideBookWizardProps) {
  const customerStep = useDispatchCustomerPhone();

  const [tripType, setTripType] = useState<TripKind>('LOCAL');
  const [scheduleMode, setScheduleMode] = useState<ScheduleMode>('RIDE_NOW');
  const [scheduledAt, setScheduledAt] = useState(localDateTimeValue());
  const [bookingFor, setBookingFor] = useState<'MYSELF' | 'OTHERS'>('MYSELF');
  const [othersName, setOthersName] = useState('');
  const [othersPhone, setOthersPhone] = useState('');
  const [outstationType, setOutstationType] = useState<'ONE_WAY' | 'ROUND_TRIP'>('ONE_WAY');
  const [returnDate, setReturnDate] = useState('');
  const [city, setCity] = useState('Bangalore');
  const [pickupLoc, setPickupLoc] = useState<LocationResult | null>(null);
  const [dropoffLoc, setDropoffLoc] = useState<LocationResult | null>(null);
  const [mapClickStep, setMapClickStep] = useState<'pickup' | 'dropoff'>('pickup');
  const [pinLoading, setPinLoading] = useState(false);

  const geo = useCurrentLocation(true);

  const [serviceId, setServiceId] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [vehicleFares, setVehicleFares] = useState<VehicleFareOption[]>([]);
  const [fareLoading, setFareLoading] = useState(false);
  const [zoneName, setZoneName] = useState('');
  const [distanceKm, setDistanceKm] = useState(0);

  const [dispatchMode, setDispatchMode] = useState<DispatchMode>('AUTOMATIC');
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const { data: transportServices = [] } = useQuery({
    queryKey: ['dispatcher-transport-services'],
    queryFn: getTenantServices,
    select: (s) => s.filter((x) => x.serviceCode === 'transportation'),
  });

  const { data: vehicleTypesRes } = useQuery({
    queryKey: ['dispatcher-vehicle-types'],
    queryFn: () => getMasters('vehicle_types', { limit: 50 }),
  });

  const pickupCoords = useMemo(() => (pickupLoc ? { lat: pickupLoc.lat, lng: pickupLoc.lng } : null), [pickupLoc]);
  const dropoffCoords = useMemo(() => (dropoffLoc ? { lat: dropoffLoc.lat, lng: dropoffLoc.lng } : null), [dropoffLoc]);

  const { data: rentalPackagesRes } = useQuery({
    queryKey: ['dispatcher-rental-packages', pickupCoords?.lat, pickupCoords?.lng, serviceId],
    queryFn: () => getBookingRentalPackages({
      lat: pickupCoords?.lat,
      lng: pickupCoords?.lng,
      serviceId,
    }),
    enabled: tripType === 'RENTAL' && !!pickupCoords && !!serviceId,
  });

  const vehicleTypes = useMemo(() => {
    const rows = (vehicleTypesRes?.data || []) as Array<{ code: string; name: string; metadata?: Record<string, unknown> }>;
    return rows.filter((v) => {
      const types = String(v.metadata?.serviceTypes || v.metadata?.serviceType || 'LOCAL,RENTAL,OUTSTATION');
      return types.includes(tripType);
    });
  }, [vehicleTypesRes, tripType]);

  const rentalPackages = useMemo((): RentalPackageOption[] => {
    const groups = (rentalPackagesRes?.packages || []) as Array<{
      packageGroupCode: string;
      packageHr: number;
      packageKm: number;
      rowIds?: string[];
      vehiclePrices: Array<{ vehicleTypeCode: string; price: number; rowId?: string }>;
    }>;
    const flat: RentalPackageOption[] = [];
    for (const g of groups) {
      for (const vp of g.vehiclePrices || []) {
        if (selectedVehicle && vp.vehicleTypeCode !== selectedVehicle) continue;
        flat.push({
          id: `${g.packageGroupCode}:${vp.vehicleTypeCode}`,
          name: `${g.packageHr}h / ${g.packageKm} km`,
          packageHr: g.packageHr,
          packageKm: g.packageKm,
          price: vp.price,
          vehicleTypeCode: vp.vehicleTypeCode,
          packageGroupCode: g.packageGroupCode,
          packageRowId: vp.rowId || g.rowIds?.[0],
        });
      }
    }
    return flat;
  }, [rentalPackagesRes, selectedVehicle]);

  useEffect(() => {
    if (transportServices[0]?._id && !serviceId) setServiceId(transportServices[0]._id);
  }, [transportServices, serviceId]);

  useEffect(() => {
    if (pickupLoc || !geo.address) return;
    setPickupLoc({ ...geo.address, label: 'My Current Location' });
    if (geo.address.city) setCity(geo.address.city);
  }, [geo.address, pickupLoc]);

  const applyCurrentAsPickup = () => {
    if (geo.address) {
      setPickupLoc({ ...geo.address, label: 'My Current Location' });
      if (geo.address.city) setCity(geo.address.city);
    } else {
      geo.load().then(() => {});
    }
  };

  useEffect(() => {
    if (pickupCoords && dropoffCoords && (tripType === 'LOCAL' || tripType === 'OUTSTATION')) {
      setDistanceKm(Math.round(haversineKm(pickupCoords.lat, pickupCoords.lng, dropoffCoords.lat, dropoffCoords.lng) * 10) / 10);
    }
  }, [pickupCoords, dropoffCoords, tripType]);

  const loadVehicleFares = useCallback(async () => {
    if (!serviceId || !pickupCoords) return;
    setFareLoading(true);
    try {
      const duration = estimateDurationMin(distanceKm || 5);
      const fares: VehicleFareOption[] = await Promise.all(
        vehicleTypes.map(async (v) => {
          try {
            const fare = await estimateTransportFare({
              serviceId,
              pickupLat: pickupCoords.lat,
              pickupLng: pickupCoords.lng,
              dropoffLat: dropoffCoords?.lat,
              dropoffLng: dropoffCoords?.lng,
              distanceKm: distanceKm || 5,
              durationMin: duration,
              scheduleType: scheduleMode === 'RIDE_LATER' ? 'ride_later' : 'ride_now',
              vehicleTypeCode: v.code,
              tripType,
              outstationType: tripType === 'OUTSTATION' ? outstationType : undefined,
            });
            return {
              code: v.code,
              name: v.name,
              capacity: Number(v.metadata?.capacity) || undefined,
              icon: String(v.metadata?.icon || ''),
              total: fare.total,
              subtotal: fare.subtotal,
              tax: fare.tax,
              zoneName: fare.zoneName,
            };
          } catch {
            return { code: v.code, name: v.name, total: 0 };
          }
        })
      );
      setVehicleFares(fares.filter((f) => f.total > 0));
      if (fares[0]?.zoneName) setZoneName(fares[0].zoneName);
      if (fares.length && !selectedVehicle) setSelectedVehicle(fares[0].code);
    } catch {
      setVehicleFares([]);
    } finally {
      setFareLoading(false);
    }
  }, [serviceId, pickupCoords, dropoffCoords, distanceKm, vehicleTypes, selectedVehicle, tripType, outstationType, scheduleMode]);

  useEffect(() => {
    if ((tripType === 'LOCAL' || tripType === 'OUTSTATION') && vehicleTypes.length && pickupCoords) {
      loadVehicleFares();
    }
  }, [tripType, outstationType, loadVehicleFares, vehicleTypes.length, pickupCoords]);

  const handleMapClick = useCallback(async (lat: number, lng: number) => {
    setPinLoading(true);
    try {
      const rev = await reverseGeocode(lat, lng);
      const loc: LocationResult = rev || {
        id: `pin_${lat}_${lng}`,
        label: mapClickStep === 'pickup' ? 'Pickup Pin' : 'Drop Pin',
        address: `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
        lat, lng,
      };
      if (mapClickStep === 'pickup') {
        setPickupLoc(loc);
        if (loc.city) setCity(loc.city);
        setMapClickStep('dropoff');
      } else {
        setDropoffLoc(loc);
      }
    } finally {
      setPinLoading(false);
    }
  }, [mapClickStep]);

  const formValid = useMemo(() => {
    if (!customerStep.isReady) return false;
    if (!pickupLoc?.lat) return false;
    if ((tripType === 'LOCAL' || tripType === 'OUTSTATION') && !dropoffLoc?.lat) return false;
    if (tripType === 'OUTSTATION' && outstationType === 'ROUND_TRIP' && !returnDate) return false;
    if (scheduleMode === 'RIDE_LATER') {
      if (!scheduledAt) return false;
      if (new Date(scheduledAt).getTime() <= Date.now()) return false;
    }
    if (bookingFor === 'OTHERS') {
      if (!othersName.trim() || !othersPhone.trim()) return false;
    }
    if (tripType === 'RENTAL') return !!selectedVehicle && !!selectedPackageId;
    return !!selectedVehicle && vehicleFares.some((v) => v.code === selectedVehicle);
  }, [customerStep.isReady, pickupLoc, dropoffLoc, tripType, outstationType, returnDate, selectedVehicle, selectedPackageId, vehicleFares, scheduleMode, scheduledAt, bookingFor, othersName, othersPhone]);

  useEffect(() => {
    setCanSubmit(formValid);
  }, [formValid, setCanSubmit]);

  const handleSubmit = useCallback(async () => {
    const next: Record<string, string> = {};
    if (!customerStep.isReady) next.customer = 'Look up or create a customer first';
    if (!pickupLoc?.lat) next.pickup = 'Pickup location is required';
    if ((tripType === 'LOCAL' || tripType === 'OUTSTATION') && !dropoffLoc?.lat) next.dropoff = 'Drop location is required';
    if (tripType === 'OUTSTATION' && outstationType === 'ROUND_TRIP' && !returnDate) next.returnDate = 'Return date is required';
    if (scheduleMode === 'RIDE_LATER') {
      if (!scheduledAt) next.scheduledAt = 'Pickup time is required for Ride Later';
      else if (new Date(scheduledAt).getTime() <= Date.now()) next.scheduledAt = 'Pickup time must be in the future';
    }
    if (bookingFor === 'OTHERS') {
      const nameErr = validatePersonName(othersName, 'Passenger name', { required: true });
      if (nameErr) next.othersName = nameErr;
      const phoneErr = validatePhoneNumber(othersPhone, 'Passenger phone', { required: true });
      if (phoneErr) next.othersPhone = phoneErr;
    }
    if (tripType === 'RENTAL') {
      if (!selectedVehicle) next.vehicle = 'Vehicle is required';
      if (!selectedPackageId) next.package = 'Rental package is required';
    } else if (!selectedVehicle) {
      next.vehicle = 'Vehicle is required';
    }
    setFieldErrors(next);
    if (Object.keys(next).length) {
      onError(Object.values(next)[0]);
      return;
    }
    setLoading(true);
    onError('');
    try {
      const customerId = await customerStep.resolveCustomerId();
      if (!pickupLoc) throw new Error('Pickup location required');
      const pickupLngLat: [number, number] = [pickupLoc.lng, pickupLoc.lat];
      const dropLngLat: [number, number] = dropoffLoc
        ? [dropoffLoc.lng, dropoffLoc.lat]
        : pickupLngLat;

      const selectedPkg = rentalPackages.find((p) => p.id === selectedPackageId);
      const payload: Record<string, unknown> = {
        customerId,
        serviceId,
        tripType,
        scheduleMode,
        bookingFor,
        dispatchMode,
        paymentMethod,
        distanceKm: distanceKm || undefined,
        durationMin: estimateDurationMin(distanceKm || 5),
        vehicleTypeCode: selectedVehicle,
        pickup: { address: pickupLoc.address, city: pickupLoc.city || city, country: 'IN', coordinates: pickupLngLat },
        dropoff: {
          address: dropoffLoc?.address || pickupLoc.address,
          city: dropoffLoc?.city || pickupLoc.city || city,
          country: 'IN',
          coordinates: dropLngLat,
        },
      };

      if (scheduleMode === 'RIDE_LATER' && scheduledAt) {
        payload.scheduledAt = new Date(scheduledAt).toISOString();
      }

      if (bookingFor === 'OTHERS') {
        payload.othersName = othersName.trim();
        payload.othersPhone = othersPhone.trim();
      }

      if (tripType === 'RENTAL' && selectedPkg) {
        if (selectedPkg.packageRowId) payload.packageId = selectedPkg.packageRowId;
        payload.rentalPackageGroupCode = selectedPkg.packageGroupCode;
        payload.packageKm = selectedPkg.packageKm;
        payload.packageHr = selectedPkg.packageHr;
      }

      if (tripType === 'OUTSTATION') {
        payload.outstationType = outstationType;
        if (outstationType === 'ROUND_TRIP' && returnDate) {
          payload.returnDate = returnDate;
        }
      }

      if (dispatchMode === 'MANUAL' && selectedDriverId) {
        payload.driverId = selectedDriverId;
      } else if (dispatchMode === 'MANUAL') {
        payload.skipAutoAssign = true;
      }

      await dispatchRide(payload);
      onSuccess();
    } catch (e: unknown) {
      onError(
        (e as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
        || (e as Error)?.message || 'Booking failed'
      );
    } finally {
      setLoading(false);
    }
  }, [
    customerStep, pickupLoc, dropoffLoc, city,
    serviceId, tripType, scheduleMode, scheduledAt, bookingFor, othersName, othersPhone, dispatchMode, paymentMethod, distanceKm, selectedVehicle,
    selectedPackageId, rentalPackages, selectedDriverId, outstationType, returnDate, setLoading, onSuccess, onError,
  ]);

  useEffect(() => {
    registerSubmit(handleSubmit);
  }, [handleSubmit, registerSubmit]);

  const selectedFare = vehicleFares.find((v) => v.code === selectedVehicle);
  const VehicleIcon = VEHICLE_ICONS[selectedVehicle] || DirectionsCar;

  const mapMarkers = useMemo((): RouteMapMarker[] => {
    const list: RouteMapMarker[] = [];
    if (geo.coords) {
      list.push({ id: 'current', lat: geo.coords.lat, lng: geo.coords.lng, label: 'You', color: '#2563eb', kind: 'current' });
    }
    if (pickupLoc) {
      list.push({ id: 'pickup', lat: pickupLoc.lat, lng: pickupLoc.lng, label: 'Pickup', color: '#22c55e', kind: 'pickup' });
    }
    if (dropoffLoc) {
      list.push({ id: 'dropoff', lat: dropoffLoc.lat, lng: dropoffLoc.lng, label: 'Drop-off', color: '#ef4444', kind: 'dropoff' });
    }
    return list;
  }, [geo.coords, pickupLoc, dropoffLoc]);

  const routeLine = useMemo(() => {
    if (!pickupLoc || !dropoffLoc || (tripType !== 'LOCAL' && tripType !== 'OUTSTATION')) return [];
    return [
      { lat: pickupLoc.lat, lng: pickupLoc.lng },
      { lat: dropoffLoc.lat, lng: dropoffLoc.lng },
    ];
  }, [pickupLoc, dropoffLoc, tripType]);

  const mapCenter = geo.coords || pickupCoords || undefined;
  const accent = '#2563eb';

  return (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1, overflow: 'hidden' }}>
      {customerStep.panel}
      {fieldErrors.customer && (
        <Typography variant="caption" color="error" fontWeight={700}>{fieldErrors.customer}</Typography>
      )}

      <Box sx={{
        flex: 1,
        minHeight: 0,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(300px, 340px)', lg: 'minmax(0, 1fr) minmax(320px, 380px)' },
        gap: 1,
        overflow: 'hidden',
      }}>
        <Box sx={{
          position: 'relative',
          minHeight: 0,
          border: '1px solid #e2e8f0',
          borderRadius: '10px',
          overflow: 'hidden',
          bgcolor: '#e2e8f0',
        }}>
          <DispatchRouteMap
            height="100%"
            chrome={false}
            center={mapCenter}
            markers={mapMarkers}
            route={routeLine}
            activePin={mapClickStep}
            onMapClick={handleMapClick}
          />
          {pinLoading && (
            <Box sx={{ position: 'absolute', inset: 0, zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha('#fff', 0.45) }}>
              <CircularProgress size={26} />
            </Box>
          )}

          <Box sx={{
            position: 'absolute',
            top: 10,
            left: 10,
            width: { xs: 'calc(100% - 20px)', sm: 'min(640px, calc(100% - 20px))', md: 'min(720px, calc(100% - 28px))' },
            maxWidth: 'calc(100% - 20px)',
            zIndex: 8,
            bgcolor: 'rgba(255,255,255,0.98)',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            p: 1.35,
            boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
          }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.25}>
              <Typography variant="caption" fontWeight={800} letterSpacing="0.08em" color="text.secondary">ROUTE</Typography>
              <ToggleButtonGroup size="small" value={mapClickStep} exclusive onChange={(_, v) => v && setMapClickStep(v)} sx={{ ...bookSegSx(accent), width: 168 }}>
                <ToggleButton value="pickup">Pickup</ToggleButton>
                <ToggleButton value="dropoff">Drop</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            <Stack spacing={1.15}>
              <Stack direction="row" spacing={0.75} alignItems="flex-start">
                <TextField size="small" label="City" value={city} onChange={(e) => setCity(e.target.value)} sx={{ width: 128, flexShrink: 0 }} />
                <Button
                  onClick={applyCurrentAsPickup}
                  disabled={geo.loading}
                  variant="outlined"
                  sx={{ minWidth: 36, height: 36, px: 0.75, borderRadius: '8px', borderColor: '#e2e8f0', color: '#0f172a' }}
                  title="Use current location"
                >
                  {geo.loading ? <CircularProgress size={16} /> : <MyLocation fontSize="small" />}
                </Button>
              </Stack>
              <LocationSearchField
                label="Pickup"
                value={pickupLoc}
                onChange={(loc) => {
                  setPickupLoc(loc);
                  setMapClickStep('pickup');
                  if (fieldErrors.pickup) {
                    setFieldErrors((prev) => {
                      const next = { ...prev };
                      delete next.pickup;
                      return next;
                    });
                  }
                }}
                bias={geo.coords}
                city={city}
                accent="#16a34a"
                iconColor="#16a34a"
                required
                multiline
                maxRows={3}
                error={!!fieldErrors.pickup}
                helperText={fieldErrors.pickup}
              />
              {(tripType === 'LOCAL' || tripType === 'OUTSTATION') && (
                <LocationSearchField
                  label="Drop-off"
                  value={dropoffLoc}
                  onChange={(loc) => {
                    setDropoffLoc(loc);
                    setMapClickStep('dropoff');
                    if (fieldErrors.dropoff) {
                      setFieldErrors((prev) => {
                        const next = { ...prev };
                        delete next.dropoff;
                        return next;
                      });
                    }
                  }}
                  bias={pickupCoords || geo.coords}
                  city={city}
                  accent="#ef4444"
                  iconColor="#ef4444"
                  required
                  multiline
                  maxRows={3}
                  error={!!fieldErrors.dropoff}
                  helperText={fieldErrors.dropoff}
                />
              )}
            </Stack>
          </Box>

          {distanceKm > 0 && (tripType === 'LOCAL' || tripType === 'OUTSTATION') && (
            <Chip
              label={`${distanceKm} km · ${estimateDurationMin(distanceKm)} min`}
              size="small"
              sx={{ position: 'absolute', bottom: 12, left: 12, zIndex: 8, fontWeight: 800, bgcolor: '#0f172a', color: '#fff' }}
            />
          )}
        </Box>

        <Box sx={{
          bgcolor: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: '10px',
          minHeight: 0,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          p: 1.25,
          gap: 1.15,
        }}>
          <Box>
            <BookRailLabel index="1" title="When · For whom" />
            <Stack spacing={1}>
              <ToggleButtonGroup
                exclusive value={scheduleMode} size="small" fullWidth
                onChange={(_, v) => {
                  if (!v) return;
                  setScheduleMode(v);
                  if (v === 'RIDE_LATER' && (!scheduledAt || new Date(scheduledAt).getTime() <= Date.now())) {
                    setScheduledAt(localDateTimeValue());
                  }
                }}
                sx={bookSegSx(accent)}
              >
                <ToggleButton value="RIDE_NOW"><Bolt fontSize="small" /> Ride Now</ToggleButton>
                <ToggleButton value="RIDE_LATER"><Schedule fontSize="small" /> Ride Later</ToggleButton>
              </ToggleButtonGroup>
              {scheduleMode === 'RIDE_LATER' && (
                <TextField
                  size="small"
                  type="datetime-local"
                  label="Pickup time"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={scheduledAt}
                  required
                  error={!!fieldErrors.scheduledAt}
                  helperText={fieldErrors.scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                />
              )}
              <ToggleButtonGroup
                exclusive value={bookingFor} size="small" fullWidth
                onChange={(_, v) => v && setBookingFor(v)}
                sx={bookSegSx(accent)}
              >
                <ToggleButton value="MYSELF"><Person fontSize="small" /> Myself</ToggleButton>
                <ToggleButton value="OTHERS"><Group fontSize="small" /> Others</ToggleButton>
              </ToggleButtonGroup>
              {bookingFor === 'OTHERS' && (
                <Stack direction="row" spacing={1}>
                  <PersonNameField
                    size="small"
                    fullWidth
                    label="Passenger name"
                    value={othersName}
                    required
                    error={!!fieldErrors.othersName}
                    helperText={fieldErrors.othersName}
                    onChange={(e) => setOthersName(e.target.value)}
                  />
                  <PhoneNumberField
                    size="small"
                    fullWidth
                    label="Passenger phone"
                    value={othersPhone}
                    required
                    error={!!fieldErrors.othersPhone}
                    helperText={fieldErrors.othersPhone}
                    onChange={(e) => setOthersPhone(e.target.value)}
                  />
                </Stack>
              )}
            </Stack>
          </Box>

          <Box>
            <BookRailLabel index="2" title="Trip type" />
            <ToggleButtonGroup
              exclusive fullWidth value={tripType} size="small"
              onChange={(_, v) => v && setTripType(v)}
              sx={bookSegSx(accent)}
            >
              <ToggleButton value="LOCAL">Local</ToggleButton>
              <ToggleButton value="RENTAL">Rental</ToggleButton>
              <ToggleButton value="OUTSTATION">Outstation</ToggleButton>
            </ToggleButtonGroup>
            {tripType === 'OUTSTATION' && (
              <Grid container spacing={1} sx={{ mt: 0.5 }}>
                <Grid item xs={outstationType === 'ROUND_TRIP' ? 6 : 12}>
                  <TextField
                    select fullWidth size="small" label="Trip mode"
                    value={outstationType}
                    onChange={(e) => setOutstationType(e.target.value as 'ONE_WAY' | 'ROUND_TRIP')}
                  >
                    <MenuItem value="ONE_WAY">One Way</MenuItem>
                    <MenuItem value="ROUND_TRIP">Round Trip</MenuItem>
                  </TextField>
                </Grid>
                {outstationType === 'ROUND_TRIP' && (
                  <Grid item xs={6}>
                    <TextField
                      fullWidth size="small" type="date" label="Return date"
                      InputLabelProps={{ shrink: true }}
                      value={returnDate}
                      required
                      error={!!fieldErrors.returnDate}
                      helperText={fieldErrors.returnDate}
                      onChange={(e) => setReturnDate(e.target.value)}
                    />
                  </Grid>
                )}
              </Grid>
            )}
            {transportServices.length > 1 && (
              <TextField select fullWidth size="small" label="Transport service" value={serviceId} onChange={(e) => setServiceId(e.target.value)} sx={{ mt: 1 }}>
                {transportServices.map((s) => <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>)}
              </TextField>
            )}
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <BookRailLabel index="3" title="Vehicle" />
            {zoneName && (
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>{zoneName}</Typography>
            )}
            {fieldErrors.vehicle && (
              <Typography variant="caption" color="error" fontWeight={700} display="block">{fieldErrors.vehicle}</Typography>
            )}
            {fareLoading && <Stack alignItems="center" py={2}><CircularProgress size={22} /></Stack>}

            {(tripType === 'LOCAL' || tripType === 'OUTSTATION') && !fareLoading && (
              <Stack spacing={0.75} sx={{ overflow: 'auto', minHeight: 0 }}>
                {vehicleFares.map((v) => {
                  const Icon = VEHICLE_ICONS[v.code] || DirectionsCar;
                  const selected = selectedVehicle === v.code;
                  return (
                    <Box
                      key={v.code}
                      onClick={() => setSelectedVehicle(v.code)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        px: 1.25,
                        py: 1,
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor: selected ? accent : '#e2e8f0',
                        bgcolor: selected ? alpha(accent, 0.06) : '#fff',
                        cursor: 'pointer',
                        '&:hover': { borderColor: selected ? accent : '#cbd5e1' },
                      }}
                    >
                      <Box sx={{
                        width: 34, height: 34, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: selected ? accent : '#f1f5f9', color: selected ? '#fff' : '#475569',
                      }}>
                        <Icon fontSize="small" />
                      </Box>
                      <Box flex={1} minWidth={0}>
                        <Typography variant="body2" fontWeight={800} noWrap>{v.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{v.capacity ? `${v.capacity} seats` : 'Standard'}</Typography>
                      </Box>
                      {selected && <CheckCircle sx={{ fontSize: 16, color: accent }} />}
                      <Typography variant="body2" fontWeight={800}>{formatCurrency(v.total)}</Typography>
                    </Box>
                  );
                })}
                {!vehicleFares.length && (
                  <Typography variant="caption" color="text.secondary">Configure zone pricing to see fares.</Typography>
                )}
              </Stack>
            )}

            {tripType === 'RENTAL' && (
              <Stack spacing={0.75} sx={{ overflow: 'auto', minHeight: 0 }}>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {vehicleTypes.map((v) => {
                    const Icon = VEHICLE_ICONS[v.code] || DirectionsCar;
                    const selected = selectedVehicle === v.code;
                    return (
                      <Box
                        key={v.code}
                        onClick={() => { setSelectedVehicle(v.code); setSelectedPackageId(''); }}
                        sx={{
                          px: 1, py: 0.85, minWidth: 88, textAlign: 'center', cursor: 'pointer',
                          borderRadius: '8px', border: '1px solid', borderColor: selected ? accent : '#e2e8f0',
                          bgcolor: selected ? alpha(accent, 0.06) : '#fff',
                        }}
                      >
                        <Icon sx={{ color: selected ? accent : '#64748b' }} fontSize="small" />
                        <Typography variant="caption" fontWeight={700} display="block">{v.name}</Typography>
                      </Box>
                    );
                  })}
                </Stack>
                {selectedVehicle && rentalPackages.map((p) => {
                  const selected = selectedPackageId === p.id;
                  return (
                    <Box
                      key={p.id}
                      onClick={() => setSelectedPackageId(p.id)}
                      sx={{
                        px: 1.25, py: 1, cursor: 'pointer', borderRadius: '8px',
                        border: '1px solid', borderColor: selected ? accent : '#e2e8f0',
                        bgcolor: selected ? alpha(accent, 0.06) : '#fff',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight={800}>{p.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{p.packageHr}h / {p.packageKm} km</Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={800}>{formatCurrency(p.price)}</Typography>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>

          {selectedFare && (tripType === 'LOCAL' || tripType === 'OUTSTATION') && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.25, py: 1, bgcolor: '#0f172a', borderRadius: '8px', color: '#fff' }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <VehicleIcon fontSize="small" />
                <Typography variant="caption" fontWeight={700} sx={{ opacity: 0.75 }}>Estimated fare</Typography>
              </Stack>
              <Typography variant="subtitle1" fontWeight={800}>{formatCurrency(selectedFare.total)}</Typography>
            </Box>
          )}

          <Box>
            <DispatchPartnerAssignStep
              compact
              partnerType="driver"
              dispatchMode={dispatchMode}
              onDispatchModeChange={setDispatchMode}
              selectedPartnerId={selectedDriverId}
              onPartnerSelect={setSelectedDriverId}
              coordinates={pickupCoords}
              vehicleTypeFilter={selectedVehicle}
            />
          </Box>
          <TextField select fullWidth size="small" required label="Payment" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
            <MenuItem value="cash">Cash</MenuItem>
            <MenuItem value="wallet">Wallet</MenuItem>
            <MenuItem value="online">Online</MenuItem>
          </TextField>
        </Box>
      </Box>
    </Box>
  );
}
